import React, { useCallback, useEffect, useState } from "react";
import client, { makeGet, makePost, makeDelete } from "@culturehq/client";
import { Button, Loader, Panel, SearchBar, Tooltip } from "@culturehq/components";

import { useProfile } from "app/context";
import { useClickOutside, useFetch, useIncrement, useSortable } from "app/hooks";
import { t, Trans } from "app/utils";

import styles from "./styles.module.scss";

const SkillCreateButton = ({ skill, onCreateUserSkill }) => {
  const onClick = async () => {
    const json = await makePost("/profile/user_skills", { name: skill.name });
    onCreateUserSkill(json.userSkill);
  };

  return (
    <Button className={[styles.create, styles.spaced].join(" ")} icon="ios-plus" onClick={onClick}>
      {skill.name}
    </Button>
  );
};

const SkillDeleteButton = ({ onDeleteUserSkill, userSkill }) => {
  const onClick = async () => {
    await makeDelete(`/profile/user_skills/${userSkill.id}`);
    onDeleteUserSkill(userSkill);
  };

  return (
    <div className={[styles.sortable, styles.spaced].join(" ")} data-id={userSkill.skill.id}>
      <div className={`sortable__drag-handler ${styles.dragger}`} />
      <Button className={styles.delete} icon="ios-checkmark" onClick={onClick}>
        {userSkill.skill.name}
      </Button>
    </div>
  );
};

const ProfileSkillsCurrent = ({ createdUserSkills, fetching, json, onDeleteUserSkill }) => {
  const sortableRef = useSortable({
    handle: `.${styles.dragger}`,
    ghostClass: styles.ghost,
    onSort(event) {
      const order = Array.from(event.to.children).map(element => (
        element.getAttribute("data-id")
      ));
      return client.reorderProfileSkills({ order });
    }
  });

  const userSkills = (json.userSkills ? json.userSkills : []).concat(createdUserSkills);

  return (
    <>
      <h2 className={styles.heading}>
        <Trans>Skills</Trans>
      </h2>
      <div className={styles.current}>
        <Loader loading={fetching}>
          <div ref={sortableRef}>
            {userSkills.map(userSkill => (
              <SkillDeleteButton
                key={userSkill.id}
                onDeleteUserSkill={onDeleteUserSkill}
                userSkill={userSkill}
              />
            ))}
          </div>
          {userSkills.length === 0 && (
            <p className={styles.placeholder}>
              <Trans>Any skills you choose will be added here</Trans>
            </p>
          )}
        </Loader>
      </div>
    </>
  );
};

const namesMatch = query => ({ name }) => name.toLowerCase() === query.toLowerCase();

const ProfileSkillsSearch = ({ onCreateUserSkill }) => {
  const [searchKey, onSearchKeyIncremented] = useIncrement();
  const [matches, setMatches] = useState([]);
  const [lastQuery, setLastQuery] = useState("");

  const onSearch = useCallback(
    async query => {
      if (query) {
        const { skills } = await client.profileAutocompleteSkills({ query });
        setMatches(skills);
      } else {
        setMatches([]);
      }
    },
    [setMatches]
  );

  const onClose = useCallback(
    () => {
      onSearchKeyIncremented();
      setMatches([]);
      setLastQuery("");
    },
    [onSearchKeyIncremented, setMatches, setLastQuery]
  );

  const onCreate = userSkill => {
    onClose();
    onCreateUserSkill(userSkill);
  };

  const containerRef = useClickOutside(onClose);

  return (
    <div ref={containerRef}>
      <SearchBar
        key={searchKey}
        autoComplete="off"
        className={styles.search}
        onSearch={onSearch}
        onSearchChange={setLastQuery}
        placeholder={t("Search all options or create a new one")}
      />
      {((matches.length > 0) || (lastQuery.length > 0)) && (
        <div className={styles.results}>
          {matches.map(skill => (
            <SkillCreateButton key={skill.name} skill={skill} onCreateUserSkill={onCreate} />
          ))}
          {lastQuery.length > 0 && !matches.some(namesMatch(lastQuery)) && (
            <SkillCreateButton
              skill={{ name: lastQuery }}
              onCreateUserSkill={onCreate}
            />
          )}
        </div>
      )}
    </div>
  );
};

const ProfileSkillsSuggestions = ({ fetching, json, onCreateUserSkill, onRefreshSuggestions }) => (
  <>
    <div className={styles.recommended}>
      <h2>
        <Trans>Recommended:</Trans>
      </h2>
      <Tooltip tip={t("After every 3 skills chosen, we'll further personalize these suggestions")}>
        <Trans>Choose 3 OR </Trans>
      </Tooltip>
      <Button icon="refresh" inverted onClick={onRefreshSuggestions}>
        <Trans>Show More</Trans>
      </Button>
    </div>

    <Loader loading={fetching}>
      {json.skills && json.skills.map(skill => (
        <SkillCreateButton key={skill.name} skill={skill} onCreateUserSkill={onCreateUserSkill} />
      ))}
    </Loader>
  </>
);

const useSuggestedSkills = (refreshClicks, skillsCreated) => {
  const [names, setNames] = useState([]);

  return useFetch(
    async () => {
      const json = await client.listProfileSuggestedSkills({ names, num: 50 });
      setNames(json.skills.map(({ name }) => name));
      return json;
    },
    [setNames, refreshClicks, skillsCreated]
  );
};

export const ProfileSkillsBody = ({ onSkillCountChange = _count => {} }) => {
  const { refreshProfile } = useProfile();
  const [skillsCreated, onSkillsCreatedIncremented] = useIncrement();
  const [refreshClicks, onRefreshClicksIncremented] = useIncrement();

  const suggestedSkillsState = useSuggestedSkills(refreshClicks, Math.floor(skillsCreated / 3));

  const userSkillsState = useFetch(() => makeGet("/profile/user_skills"));
  const [createdUserSkills, setCreatedUserSkills] = useState([]);

  const onCreateUserSkill = userSkill => {
    onSkillsCreatedIncremented();
    suggestedSkillsState.onUpdate(({ skills }) => ({
      skills: (skills || []).filter(
        current => current.name !== userSkill.skill.name
      )
    }));
    setCreatedUserSkills(state => [...state, userSkill]);
    refreshProfile();
  };

  const onDeleteUserSkill = userSkill => {
    setCreatedUserSkills(userSkills => userSkills.filter(current => (
      current.id !== userSkill.id
    )));

    userSkillsState.onUpdate(({ userSkills }) => ({
      userSkills: userSkills.filter(current => (
        current.id !== userSkill.id
      ))
    }));
    refreshProfile();
  };

  useEffect(
    () => {
      if (!userSkillsState.fetching) {
        onSkillCountChange(
          (userSkillsState.json.userSkills?.length || 0) + createdUserSkills.length
        );
      }
    },
    [userSkillsState, createdUserSkills, onSkillCountChange]
  );

  return (
    <>
      <p>
        <Trans>
          Which skills and what knowledge can you help the community with?
          Highlighting these will showcase your strengths and give others
          a sense of your experience.
        </Trans>
      </p>
      <ProfileSkillsCurrent
        {...userSkillsState}
        createdUserSkills={createdUserSkills}
        onDeleteUserSkill={onDeleteUserSkill}
      />
      <ProfileSkillsSearch onCreateUserSkill={onCreateUserSkill} />
      <ProfileSkillsSuggestions
        {...suggestedSkillsState}
        onCreateUserSkill={onCreateUserSkill}
        onRefreshSuggestions={onRefreshClicksIncremented}
      />
    </>
  );
};

const ProfileSkills = () => (
  <Panel>
    <Panel.Heading>
      <Trans>Select your skills</Trans>
    </Panel.Heading>
    <Panel.Body>
      <ProfileSkillsBody />
    </Panel.Body>
  </Panel>
);

export default ProfileSkills;
